import Dexie from 'dexie';
import md5 from "blueimp-md5"
import electronBridge from './utils/electronBridge';

const ipcRenderer = electronBridge.ipcRenderer;
electronBridge.log("React - DB.js")
  
  /** Tries to convert to persisted storage.
    @returns {Promise<boolean>} Promise resolved with true if successfully
    persisted the storage, false if not, and undefined if the API is not present.
  */
  async function persist() {
    return await navigator.storage && navigator.storage.persist ?
      navigator.storage.persist() :
      undefined;
  }
  
  /** Queries available disk quota.
    @see https://developer.mozilla.org/en-US/docs/Web/API/StorageEstimate
    @returns {Promise<{quota: number, usage: number}>} Promise resolved with
    {quota: number, usage: number} or undefined.
  */
  async function showEstimatedQuota() {
    return await navigator.storage && navigator.storage.estimate ?
      navigator.storage.estimate() :
      undefined;
  }

  showEstimatedQuota().then(estimate => {
    if (estimate) {
      console.log("Estimated Quota: ", estimate?.quota);
      console.log("Quota Used: ", estimate?.usage);
    }
}).catch(err => {
    console.log("Could not get storage estimate:", err);
});
  
  /** Tries to persist storage without ever prompting user.
    @returns {Promise<string>}
      "never" In case persisting is not ever possible. Caller don't bother
        asking user for permission.
      "prompt" In case persisting would be possible if prompting user first.
      "persisted" In case this call successfully silently persisted the storage,
        or if it was already persisted.
  */
  async function tryPersistWithoutPromtingUser() {
    if (!navigator.storage || !navigator.storage.persisted) {
      return "never";
    }
    let persisted = await navigator.storage.persisted();
    if (persisted) {
      return "persisted";
    }
    if (!navigator.permissions || !navigator.permissions.query) {
      return "prompt"; // It MAY be successful to prompt. Don't know.
    }
    const permission = await navigator.permissions.query({
      name: "persistent-storage"
    });
    if (permission.state === "granted") {
      persisted = await navigator.storage.persist();
      if (persisted) {
        return "persisted";
      } else {
        throw new Error("Failed to persist");
      }
    }
    if (permission.state === "prompt") {
      return "prompt";
    }
    return "never";
  }

  async function initStoragePersistence() {
    const persist = await tryPersistWithoutPromtingUser();
    switch (persist) {
      case "never":
        console.log("Not possible to persist storage");
        break;
      case "persisted":
        console.log("Successfully persisted storage silently");
        break;
      case "prompt":
        console.log("Not persisted, but we may prompt user when we want to.");
        break;
    }
  }

  initStoragePersistence();

export const db = new Dexie('promptus-pwa-db');

async function checkAndRepairTables() {
  try {
    await db.open();
    console.log("Database opened successfully");
    electronBridge.log("Database opened successfully");

    // Iterate over each table and try to read data
    for (const table of db.tables) {
      try {
        await table.toCollection().limit(1).toArray();
        console.log(`Table ${table.name} is healthy.`);
        electronBridge.log(`Table ${table.name} is healthy.`);

      } catch (error) {
        console.error(`Error in table ${table.name}:`, error);
        electronBridge.log( `Error in table ${table.name}:`, error);

        console.warn(`Attempting to delete corrupted table: ${table.name}`);
        electronBridge.log( `Attempting to delete corrupted table: ${table.name}`);
        try {
          // await table.clear();
          console.log(`Table ${table.name} cleared successfully. (WIP)`);
          electronBridge.log( `Table ${table.name} cleared successfully. (WIP)`);

        } catch (clearError) {
          console.error(`Failed to clear table ${table.name}:`, clearError);
          electronBridge.log( `Failed to clear table ${table.name}:`, clearError);
        }
      }
    }
    
    // Check if mommChats table exists, if not create it
    const mommChatsExists = db.tables.some(table => table.name === 'mommChats');
    if (!mommChatsExists) {
      console.log("mommChats table doesn't exist in database, creating it");
      electronBridge.log( "mommChats table doesn't exist, creating it");
      
      try {
        // Create a new version with the mommChats table
        db.version(db.verno + 1).stores({
          mommChats: '++id, timestamp, messages'
        });
        
        // Reopen the database to apply the changes
        await db.open();
        console.log("mommChats table created successfully");
        electronBridge.log( "mommChats table created successfully");
      } catch (tableError) {
        console.error("Error creating mommChats table:", tableError);
        electronBridge.log( `Error creating mommChats table: ${tableError}`);
      }
    }
  } catch (error) {
    console.error("Error opening database:", error);
    electronBridge.log( "Error opening database:", error);

    if (error.name === "InvalidStateError" || error.name === "VersionError") {
      console.warn("Detected possible IndexedDB corruption. Attempting to delete and recreate the database.");
      electronBridge.log( "Detected possible IndexedDB corruption. Attempting to delete and recreate the database.");

      try {
        // await Dexie.delete('promptus-pwa-db');
        console.log("Database deleted successfully. Recreating... (WIP)");
        electronBridge.log( "Database deleted successfully. Recreating... (WIP)");
        window.location.reload();
      } catch (deleteError) {
        console.error("Error deleting corrupted database:", deleteError);
        electronBridge.log( "Error deleting corrupted database:", deleteError);
      }
    }
  }
}

async function initDatabase() {
  try {
    db.version(1).stores({
        collection: '++id, &uid, prompt, queue, timestamp',
        images: '++id, uid, image, &seed, inCollection, model',
    });
    db.version(2).stores({
        collection: '++id, &uid, prompt, queue, timestamp',
        images: '++id, uid, image, seed, inCollection, model',
    });
    db.version(3).stores({
        collection: '++id, &uid, prompt, queue, timestamp',
        images: '++id, uid, image, seed, inCollection, model',
        settings: '&key, value'
    });
    db.version(4).stores({
        collection: '++id, &uid, prompt, queue, timestamp',
        images: '++id, uid, image, seed, inCollection, model, &hash',
        settings: '&key, value'
    }).upgrade(trans => {
        return trans.table("images").toCollection().toArray(( async images => {
            for (const image of images) {
                if (!image.hash) {
                    await db.images.toArray(oldImages => {
                        oldImages.filter(dupImage => dupImage.image === image.image).forEach((duplicate, i) => {
                            if (i > 0) {
                                db.images.where('id').equals(duplicate.id).delete();
                            }
                        });
                    });
                    await db.images.put({...image, hash: md5(image.image)})
                }
            }
        }))
    });
    
    db.version(5).stores({
        collection: '++id, &uid, prompt, queue, timestamp',
        images: '++id, uid, image, seed, inCollection, model, &hash',
        collectedImages: '++id, uid, image, seed, model, &hash',
        settings: '&key, value'
    }).upgrade(trans => {
        return trans.table("images").toCollection().toArray(async images => {
            if (images.length > 0) {
                for (const image of images) {
                    if (image.inCollection) {
                        // Check if image already exists before migrating
                        const existing = await trans.table("collectedImages").where('hash').equals(image.hash).first();
                        if (!existing) {
                            await trans.table("collectedImages").put({
                                uid: image.uid,
                                image: image.image,
                                seed: image.seed,
                                model: image.model,
                                hash: image.hash
                            });
                        }
                    }
                }
            }
        })
    })
  } catch (error) {
    console.error('Error initializing Dexie upgrade database:', error);
    electronBridge.log( "React - Dexie Upgrade Error = " + error)
  }
  try {
    db.version(6).stores({
        collection: '++id, &uid, prompt, queue, timestamp',
        images: '++id, uid, image, seed, inCollection, model, &hash, online, tag',
        collectedImages: '++id, uid, image, seed, model, &hash',
        settings: '&key, value'
    })
    db.version(7).stores({
        collection: '++id, &uid, prompt, queue, timestamp',
        images: '++id, uid, image, seed, inCollection, model, &hash, online, tag',
        collectedImages: '++id, uid, image, seed, model, &hash, online, tag',
        settings: '&key, value'
    })
    db.version(8).stores({
        collection: '++id, &uid, prompt, queue, timestamp',
        images: '++id, uid, image, seed, inCollection, model, &hash, online, tag',
        collectedImages: '++id, uid, image, seed, model, &hash, online, tag',
        settings: '&key, value',
        account: '&key, value'
    })
    db.version(9).stores({
        collection: '++id, &uid, prompt, queue, timestamp',
        images: '++id, uid, image, seed, inCollection, model, &hash, online, tag',
        collectedImages: '++id, uid, image, seed, model, &hash, online, tag, prompt',
        settings: '&key, value',
        account: '&key, value'
    })
    db.version(10).stores({
        collection: '++id, &uid, prompt, queue, timestamp',
        images: '++id, uid, image, seed, inCollection, model, &hash, online, tag',
        collectedImages: '++id, uid, image, seed, model, &hash, online, tag, prompt, public',
        settings: '&key, value',
        account: '&key, value'
    })
    db.version(11).stores({
        collection: '++id, &uid, prompt, queue, timestamp',
        images: '++id, uid, image, seed, inCollection, model, &hash, online, tag',
        collectedImages: '++id, uid, image, seed, model, &hash, online, tag, prompt, public',
        settings: '&key, value',
        account: '&key, value',
        history: '++id, seed, prompt'
    })
    db.version(12).stores({
      collection: '++id, &uid, prompt, queue, timestamp',
      images: '++id, uid, image, seed, inCollection, model, &hash, online, tag',
      collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public',
      settings: '&key, value',
      account: '&key, value',
      history: '++id, seed, prompt'
    })
    db.version(13).stores({
        collection: '++id, &uid, prompt, queue, timestamp',
        images: '++id, uid, image, seed, inCollection, model, &hash, online, tag',
        collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public',
        settings: '&key, value',
        account: '&key, value',
        history: '++id, seed, prompt',
        reloadImages: 'reload'
    })
    
    db.version(14).stores({
      collection: '++id, &uid, prompt, queue, timestamp',
      images: '++id, uid, image, seed, inCollection, model, &hash, online, tag',
      collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public',
      settings: '&key, value',
      account: '&key, value',
      history: '++id, seed, prompt',
      reloadImages: 'reload',
      imageDescriptions: '++id, description, hash, timestamp'
    })
    
    db.version(15).stores({
      collection: '++id, &uid, prompt, queue, timestamp',
      images: '++id, uid, image, seed, inCollection, model, &hash, online, tag',
      collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public',
      settings: '&key, value',
      account: '&key, value',
      history: '++id, seed, prompt',
      reloadImages: 'reload',
      imageDescriptions: '++id, description, hash, timestamp',
      recommendedSettings: '++id,settingId, &model, steps, guidance, imageCount, imageWidth, imageHeight, scheduler, tile, shuffle, upscale'
    })
    
    db.version(16).stores({
      collection: '++id, &uid, prompt, queue, timestamp',
      images: '++id, uid, image, seed, inCollection, model, &hash, online, tag',
      collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public',
      settings: '&key, value',
      account: '&key, value',
      history: '++id, seed, prompt',
      reloadImages: 'reload',
      imageDescriptions: '++id, description, hash, timestamp',
      recommendedSettings: '++id,&model, steps, guidance, imageCount, imageWidth, imageHeight, scheduler, tile, shuffle, upscale'
    })
    
    db.version(17).stores({
      collection: '++id, &uid, prompt, queue, timestamp',
      images: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt',
      collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public',
      settings: '&key, value',
      account: '&key, value',
      history: '++id, seed, prompt',
      reloadImages: 'reload',
      imageDescriptions: '++id, description, hash, timestamp',
      recommendedSettings: '++id,&model, steps, guidance, imageCount, imageWidth, imageHeight, scheduler, tile, shuffle, upscale'
    })
    
    db.version(18).stores({
      collection: '++id, &uid, prompt, queue, timestamp',
      images: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp',
      collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public',
      settings: '&key, value',
      account: '&key, value',
      history: '++id, seed, prompt',
      reloadImages: 'reload',
      imageDescriptions: '++id, description, hash, timestamp',
      recommendedSettings: '++id,&model, steps, guidance, imageCount, imageWidth, imageHeight, scheduler, tile, shuffle, upscale'
    })
    
    db.version(19).stores({
      collection: '++id, &uid, prompt, queue, timestamp',
      images: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      outputImages: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public, dataType',
      settings: '&key, value',
      account: '&key, value',
      history: '++id, seed, prompt',
      reloadImages: 'reload',
      imageDescriptions: '++id, description, hash, timestamp',
      recommendedSettings: '++id,&model, steps, guidance, imageCount, imageWidth, imageHeight, scheduler, tile, shuffle, upscale',
      mommChats: '++id, timestamp, messages'
    })
    
    // Also ensure we have a version 20 for forward compatibility with mommChats table
    db.version(20).stores({
      collection: '++id, &uid, prompt, queue, timestamp',
      images: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      outputImages: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public, dataType',
      settings: '&key, value',
      account: '&key, value',
      history: '++id, seed, prompt',
      reloadImages: 'reload',
      imageDescriptions: '++id, description, hash, timestamp',
      recommendedSettings: '++id,&model, steps, guidance, imageCount, imageWidth, imageHeight, scheduler, tile, shuffle, upscale',
      mommChats: '++id, timestamp, messages'
    })
    
    // Add version 21 with new collection management tables
    db.version(21).stores({
      collection: '++id, &uid, prompt, queue, timestamp',
      images: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      outputImages: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public, dataType',
      settings: '&key, value',
      account: '&key, value',
      history: '++id, seed, prompt',
      reloadImages: 'reload',
      imageDescriptions: '++id, description, hash, timestamp',
      recommendedSettings: '++id,&model, steps, guidance, imageCount, imageWidth, imageHeight, scheduler, tile, shuffle, upscale',
      mommChats: '++id, timestamp, messages',
      collections: '++id, name, description, createdAt, updatedAt, imageCount',
      collectionImages: '++id, collectionId, imageUrl, prompt, addedAt'
    })
    
    // Add version 22 with timestamp field and compound indexes for collectedImages
    db.version(22).stores({
      collection: '++id, &uid, prompt, queue, timestamp',
      images: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      outputImages: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public, dataType, timestamp, [public+timestamp], [tag+timestamp], [model+timestamp]',
      settings: '&key, value',
      account: '&key, value',
      history: '++id, seed, prompt',
      reloadImages: 'reload',
      imageDescriptions: '++id, description, hash, timestamp',
      recommendedSettings: '++id,&model, steps, guidance, imageCount, imageWidth, imageHeight, scheduler, tile, shuffle, upscale',
      mommChats: '++id, timestamp, messages',
      collections: '++id, name, description, createdAt, updatedAt, imageCount',
      collectionImages: '++id, collectionId, imageUrl, prompt, addedAt'
    }).upgrade(trans => {
      // Add timestamp to existing records that don't have it
      return trans.collectedImages.toCollection().modify(image => {
        if (!image.timestamp) {
          image.timestamp = Date.now();
        }
      });
    })
    
    // Add version 23 to remove problematic compound indexes
    db.version(23).stores({
      collection: '++id, &uid, prompt, queue, timestamp',
      images: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      outputImages: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public, dataType, timestamp',
      settings: '&key, value',
      account: '&key, value',
      history: '++id, seed, prompt',
      reloadImages: 'reload',
      imageDescriptions: '++id, description, hash, timestamp',
      recommendedSettings: '++id,&model, steps, guidance, imageCount, imageWidth, imageHeight, scheduler, tile, shuffle, upscale',
      mommChats: '++id, timestamp, messages',
      collections: '++id, name, description, createdAt, updatedAt, imageCount',
      collectionImages: '++id, collectionId, imageUrl, prompt, addedAt'
    })
    
    // Add version 24 with cosyflows table
    db.version(24).stores({
      collection: '++id, &uid, prompt, queue, timestamp',
      images: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      outputImages: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public, dataType, timestamp',
      settings: '&key, value',
      account: '&key, value',
      history: '++id, seed, prompt',
      reloadImages: 'reload',
      imageDescriptions: '++id, description, hash, timestamp',
      recommendedSettings: '++id,&model, steps, guidance, imageCount, imageWidth, imageHeight, scheduler, tile, shuffle, upscale',
      mommChats: '++id, timestamp, messages',
      collections: '++id, name, description, createdAt, updatedAt, imageCount',
      collectionImages: '++id, collectionId, imageUrl, prompt, addedAt',
      cosyflows: '++id, data, timestamp'
    })

    // Add version 25 with indexed cosyflows table for optimized lookups
    db.version(25).stores({
      collection: '++id, &uid, prompt, queue, timestamp',
      images: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      outputImages: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public, dataType, timestamp',
      settings: '&key, value',
      account: '&key, value',
      history: '++id, seed, prompt',
      reloadImages: 'reload',
      imageDescriptions: '++id, description, hash, timestamp',
      recommendedSettings: '++id,&model, steps, guidance, imageCount, imageWidth, imageHeight, scheduler, tile, shuffle, upscale',
      mommChats: '++id, timestamp, messages',
      collections: '++id, name, description, createdAt, updatedAt, imageCount',
      collectionImages: '++id, collectionId, imageUrl, prompt, addedAt',
      cosyflows: '++id, data.title, data, timestamp'
    }).upgrade(trans => {
      // Migration logic to ensure data integrity during index creation
      console.log('[DB Migration] Starting cosyflows index migration to version 25...');
      electronBridge.log('[DB Migration] Starting cosyflows index migration to version 25...');

      return trans.cosyflows.toCollection().modify(record => {
        // Validate data structure and ensure data.title exists
        if (record.data && typeof record.data === 'object') {
          if (!record.data.title) {
            console.warn('[DB Migration] Found record without data.title, setting default:', record.id);
            electronBridge.log('[DB Migration] Found record without data.title, setting default: ' + record.id);
            record.data.title = `Unknown Model ${record.id}`;
          }
        } else {
          console.warn('[DB Migration] Found record with invalid data structure:', record.id);
          electronBridge.log('[DB Migration] Found record with invalid data structure: ' + record.id);
          record.data = { title: `Invalid Model ${record.id}` };
        }
      }).then(() => {
        console.log('[DB Migration] Successfully completed cosyflows index migration to version 25');
        electronBridge.log('[DB Migration] Successfully completed cosyflows index migration to version 25');
      }).catch(error => {
        console.error('[DB Migration] Error during cosyflows migration:', error);
        electronBridge.log('[DB Migration] Error during cosyflows migration: ' + error);
        throw error;
      });
    })

    // Add version 26 with CRITICAL PERFORMANCE INDEXES for gallery loading
    // This version adds indexes for receivedtimestamp, requestedtimestamp, and dataType
    // to eliminate full table scans during gallery loading operations
    db.version(26).stores({
      collection: '++id, &uid, prompt, queue, timestamp',
      // IMPROVED: Added indexes for timestamp fields and compound indexes for common queries
      images: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType, [tag+receivedtimestamp], [model+receivedtimestamp], [dataType+receivedtimestamp]',
      outputImages: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public, dataType, timestamp',
      settings: '&key, value',
      account: '&key, value',
      history: '++id, seed, prompt',
      reloadImages: 'reload',
      imageDescriptions: '++id, description, hash, timestamp',
      recommendedSettings: '++id,&model, steps, guidance, imageCount, imageWidth, imageHeight, scheduler, tile, shuffle, upscale',
      mommChats: '++id, timestamp, messages',
      collections: '++id, name, description, createdAt, updatedAt, imageCount',
      collectionImages: '++id, collectionId, imageUrl, prompt, addedAt',
      cosyflows: '++id, data.title, data, timestamp'
    }).upgrade(trans => {
      // Migration logic for gallery performance improvements
      console.log('[DB Migration] Starting gallery performance index migration to version 26...');
      electronBridge.log('[DB Migration] Starting gallery performance index migration to version 26...');

      // No data transformation needed, just index creation
      // The new indexes will be built automatically by Dexie

      console.log('[DB Migration] Successfully completed gallery performance migration to version 26');
      electronBridge.log('[DB Migration] Successfully completed gallery performance migration to version 26');
    })

    // Add version 27 with fixed history table schema
    db.version(27).stores({
      collection: '++id, &uid, prompt, queue, timestamp',
      images: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType, [tag+receivedtimestamp], [model+receivedtimestamp], [dataType+receivedtimestamp]',
      outputImages: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public, dataType, timestamp',
      settings: '&key, value',
      account: '&key, value',
      history: '++id, seeds, prompt, timestamp',  // FIXED: Changed from 'seed' to 'seeds' and added 'timestamp'
      reloadImages: 'reload',
      imageDescriptions: '++id, description, hash, timestamp',
      recommendedSettings: '++id,&model, steps, guidance, imageCount, imageWidth, imageHeight, scheduler, tile, shuffle, upscale',
      mommChats: '++id, timestamp, messages',
      collections: '++id, name, description, createdAt, updatedAt, imageCount',
      collectionImages: '++id, collectionId, imageUrl, prompt, addedAt',
      cosyflows: '++id, data.title, data, timestamp'
    }).upgrade(trans => {
      // Migration logic to convert existing single 'seed' values to 'seeds' arrays
      console.log('[DB Migration] Starting history table migration to version 27...');
      electronBridge.log('[DB Migration] Starting history table migration to version 27...');

      return trans.history.toCollection().modify(record => {
        // If record has old 'seed' field but no 'seeds' field, convert it
        if (record.seed !== undefined && !record.seeds) {
          record.seeds = record.seed ? [String(record.seed)] : [];
          // Remove the old 'seed' field
          delete record.seed;
        }

        // Ensure seeds is always an array
        if (!Array.isArray(record.seeds)) {
          record.seeds = record.seeds ? [String(record.seeds)] : [];
        }

        // Add timestamp if missing
        if (!record.timestamp) {
          record.timestamp = Date.now();
        }
      }).then(() => {
        console.log('[DB Migration] Successfully completed history table migration to version 27');
        electronBridge.log('[DB Migration] Successfully completed history table migration to version 27');
      }).catch(error => {
        console.error('[DB Migration] Error during history migration:', error);
        electronBridge.log('[DB Migration] Error during history migration: ' + error);
        throw error;
      });
    })

    // Version 30: Server-based model storage - one entry per server with models array
    db.version(30).stores({
      collection: '++id, &uid, prompt, queue, timestamp',
      images: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType, [tag+receivedtimestamp], [model+receivedtimestamp], [dataType+receivedtimestamp]',
      outputImages: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public, dataType, timestamp',
      settings: '&key, value',
      account: '&key, value',
      history: '++id, seeds, prompt, timestamp',
      reloadImages: 'reload',
      imageDescriptions: '++id, description, hash, timestamp',
      recommendedSettings: '++id,&model, steps, guidance, imageCount, imageWidth, imageHeight, scheduler, tile, shuffle, upscale',
      mommChats: '++id, timestamp, messages',
      collections: '++id, name, description, createdAt, updatedAt, imageCount',
      collectionImages: '++id, collectionId, imageUrl, prompt, addedAt',
      cosyflows: '++id, data.title, data, timestamp',
      offlineModels: '++id, &[serverType+serverUrl], serverType, serverUrl, models, lastUpdated'
    }).upgrade(async trans => {
      console.log('[DB Migration] Migrating to server-based model storage...');
      electronBridge.log('[DB Migration] Migrating to server-based model storage...');

      try {
        // Migrate from individual model entries (if they exist from version 29)
        const existingModels = await trans.table('offlineModels').toArray();
        const serverModelMap = new Map();

        // Group existing individual models by server
        for (const modelEntry of existingModels) {
          if (modelEntry.serverType && modelEntry.serverUrl && modelEntry.modelName) {
            const serverKey = `${modelEntry.serverType}|${modelEntry.serverUrl}`;

            if (!serverModelMap.has(serverKey)) {
              serverModelMap.set(serverKey, {
                serverType: modelEntry.serverType,
                serverUrl: modelEntry.serverUrl,
                models: [],
                lastUpdated: modelEntry.lastUpdated || new Date().toISOString()
              });
            }

            const serverData = serverModelMap.get(serverKey);
            serverData.models.push(modelEntry.modelName);

            // Keep the most recent lastUpdated time
            if (modelEntry.lastUpdated && new Date(modelEntry.lastUpdated) > new Date(serverData.lastUpdated)) {
              serverData.lastUpdated = modelEntry.lastUpdated;
            }
          }
        }

        // Clear old table and add server-based entries
        await trans.table('offlineModels').clear();

        for (const serverData of serverModelMap.values()) {
          await trans.table('offlineModels').add(serverData);
        }

        console.log(`[DB Migration] Migrated ${serverModelMap.size} servers with models`);

        // Also migrate from settings if no existing model entries
        if (serverModelMap.size === 0) {
          const existingModelSettings = await trans.table('settings')
            .where('key').anyOf(['local_gpu_models', 'local_cpu_models', 'local_cosy_models'])
            .toArray();

          for (const setting of existingModelSettings) {
            if (setting.value && typeof setting.value === 'string') {
              try {
                const serverType = setting.key.replace('local_', '').replace('_models', '');
                const models = JSON.parse(setting.value || '[]');

                const serverUrlMap = {
                  gpu: 'http://localhost:7111',
                  cpu: 'http://localhost:7112',
                  cosy: 'http://localhost:8190'
                };

                if (Array.isArray(models) && models.length > 0) {
                  const modelNames = models.map(model =>
                    typeof model === 'string' ? model : (model.name || model.title || 'Unknown')
                  );

                  await trans.table('offlineModels').add({
                    serverType,
                    serverUrl: serverUrlMap[serverType] || 'http://localhost:7111',
                    models: modelNames,
                    lastUpdated: new Date().toISOString()
                  });
                }
              } catch (parseError) {
                console.warn(`[MIGRATION] Could not parse model data for ${setting.key}:`, parseError);
              }
            }
          }
        }

        console.log('[DB Migration] Successfully migrated to server-based model storage');
        electronBridge.log('[DB Migration] Successfully migrated to server-based model storage');
      } catch (error) {
        console.warn('[DB Migration] Model data migration failed (non-critical):', error);
        electronBridge.log(`[DB Migration] Model data migration failed: ${error}`);
      }
    })

    // Add versions 240-260 to handle existing databases with higher version numbers
    // Updated to use the new PERFORMANCE-OPTIMIZED schema with FIXED history table and offlineModels
    const currentSchema = {
      collection: '++id, &uid, prompt, queue, timestamp',
      // PERFORMANCE OPTIMIZED: Includes all critical indexes for fast gallery loading
      images: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType, [tag+receivedtimestamp], [model+receivedtimestamp], [dataType+receivedtimestamp]',
      outputImages: '++id, uid, image, seed, inCollection, model, &hash, online, tag, prompt, receivedtimestamp, requestedtimestamp, dataType',
      collectedImages: '++id, imageID, uid, image, seed, model, &hash, online, tag, prompt, public, dataType, timestamp',
      settings: '&key, value',
      account: '&key, value',
      history: '++id, seeds, prompt, timestamp',  // FIXED: Uses correct schema
      reloadImages: 'reload',
      imageDescriptions: '++id, description, hash, timestamp',
      recommendedSettings: '++id,&model, steps, guidance, imageCount, imageWidth, imageHeight, scheduler, tile, shuffle, upscale',
      mommChats: '++id, timestamp, messages',
      collections: '++id, name, description, createdAt, updatedAt, imageCount',
      collectionImages: '++id, collectionId, imageUrl, prompt, addedAt',
      cosyflows: '++id, data.title, data, timestamp',
      offlineModels: '++id, &[serverType+serverUrl], serverType, serverUrl, models, lastUpdated',
      defaultModels: '&model, weighting, dateUpdated'
    };

    // Version 31: Add defaultModels table for API-driven default model weights
    db.version(31).stores(currentSchema)

    // Add versions 240-300 with the indexed schema to handle version mismatches
    // Extended to 300 to ensure defaultModels table is created on existing databases
    for (let version = 240; version <= 300; version++) {
      db.version(version).stores(currentSchema);
    }
  } catch (error) {
    console.error('Error initializing Dexie database:', error);
    electronBridge.log( "React - Dexie Error = " + error)
  }
  
  await checkAndRepairTables();
}

initDatabase()

// Comprehensive Data Validation System for Cosyflows
// ====================================================

/**
 * JSON Schema for Cosyflow Data Structure
 * Defines the expected structure and validation rules
 */
const COSYFLOW_SCHEMA = {
  type: 'object',
  required: ['data', 'timestamp'],
  properties: {
    id: { type: 'number' },
    data: {
      type: 'object',
      required: ['title'],
      properties: {
        title: {
          type: 'string',
          minLength: 1,
          maxLength: 500,
          pattern: '^[\\s\\S]*\\S[\\s\\S]*$' // Must contain at least one non-whitespace character
        },
        variables: {
          type: 'object',
          additionalProperties: {
            type: 'object',
            required: ['type'],
            properties: {
              type: {
                type: 'string',
                enum: ['seed', 'BOOLEAN', 'boolean', 'BOOL', 'bool', 'INT', 'int', 'number', 'STRING', 'string', 'text', 'image', 'mask_image', 'ENUM', 'enum', 'SELECT', 'select']
              },
              default: true, // Allow any type for default value
              min: { type: 'number' },
              max: { type: 'number' },
              step: { type: 'number' },
              options: { type: 'array' },
              description: { type: 'string' },
              placeholder: { type: 'string' }
            }
          }
        },
        // Allow additional properties for extensibility
        description: { type: 'string' },
        category: { type: 'string' },
        version: { type: 'string' }
      }
    },
    timestamp: {
      type: 'number',
      minimum: 0
    }
  }
};

/**
 * Validates if a value matches the specified variable type
 * @param {*} value - The value to validate
 * @param {string} type - The expected type
 * @returns {object} - { valid: boolean, error?: string, correctedValue?: any }
 */
function validateVariableValue(value, type) {
  if (value === null || value === undefined) {
    return { valid: true }; // Allow null/undefined values
  }

  switch (type?.toLowerCase()) {
    case 'boolean':
    case 'bool':
      if (typeof value === 'boolean') return { valid: true };
      if (value === 1 || value === 0) return { valid: true, correctedValue: Boolean(value) };
      if (value === 'true') return { valid: true, correctedValue: true };
      if (value === 'false') return { valid: true, correctedValue: false };
      return { valid: false, error: `Expected boolean, got ${typeof value}: ${value}` };

    case 'int':
    case 'number':
      if (typeof value === 'number' && !isNaN(value)) return { valid: true };
      const numValue = Number(value);
      if (!isNaN(numValue)) return { valid: true, correctedValue: numValue };
      return { valid: false, error: `Expected number, got ${typeof value}: ${value}` };

    case 'string':
    case 'text':
      if (typeof value === 'string') return { valid: true };
      return { valid: true, correctedValue: String(value) }; // Auto-convert to string

    case 'seed':
      // Seeds should be numbers or numeric strings
      if (typeof value === 'number' && value >= 0) return { valid: true };
      const seedValue = Number(value);
      if (!isNaN(seedValue) && seedValue >= 0) return { valid: true, correctedValue: seedValue };
      return { valid: false, error: `Seed must be a non-negative number, got: ${value}` };

    case 'image':
    case 'mask_image':
      // Images can be base64 strings, URLs, or null
      if (typeof value === 'string' || value === null) return { valid: true };
      return { valid: false, error: `Image must be a string or null, got ${typeof value}` };

    case 'enum':
    case 'select':
      // For enum/select types, any value is technically valid as it depends on options
      return { valid: true };

    default:
      // Unknown type - allow it but log warning
      console.warn(`[Validation] Unknown variable type: ${type}, allowing value`);
      return { valid: true };
  }
}

/**
 * Comprehensive validation for cosyflow data structure
 * @param {object} data - The cosyflow data to validate
 * @param {object} options - Validation options
 * @returns {object} - { valid: boolean, errors: string[], warnings: string[], correctedData?: object }
 */
export function validateCosyflowData(data, options = {}) {
  const { autoCorrect = true, strict = false } = options;
  const errors = [];
  const warnings = [];
  let correctedData = autoCorrect ? JSON.parse(JSON.stringify(data)) : null;

  try {
    // Basic structure validation
    if (!data || typeof data !== 'object') {
      errors.push('Data must be an object');
      return { valid: false, errors, warnings };
    }

    // Validate required fields
    if (!data.data || typeof data.data !== 'object') {
      errors.push('Missing or invalid "data" property');
    } else {
      // Validate title
      if (!data.data.title || typeof data.data.title !== 'string') {
        errors.push('Missing or invalid "data.title" property');
      } else if (data.data.title.trim().length === 0) {
        errors.push('Title cannot be empty or just whitespace');
      } else if (data.data.title.length > 500) {
        errors.push('Title exceeds maximum length of 500 characters');
      }

      // Validate variables if present
      if (data.data.variables !== undefined) {
        if (typeof data.data.variables !== 'object' || data.data.variables === null) {
          errors.push('Variables must be an object or undefined');
        } else {
          // Validate each variable
          for (const [key, variable] of Object.entries(data.data.variables)) {
            if (!variable || typeof variable !== 'object') {
              errors.push(`Variable "${key}" must be an object`);
              continue;
            }

            // Validate variable type
            if (!variable.type || typeof variable.type !== 'string') {
              errors.push(`Variable "${key}" missing or invalid type`);
              continue;
            }

            // Validate default value against type
            if ('default' in variable) {
              const validation = validateVariableValue(variable.default, variable.type);
              if (!validation.valid) {
                if (strict) {
                  errors.push(`Variable "${key}": ${validation.error}`);
                } else {
                  warnings.push(`Variable "${key}": ${validation.error}`);
                  if (autoCorrect && validation.correctedValue !== undefined && correctedData) {
                    correctedData.data.variables[key].default = validation.correctedValue;
                  }
                }
              } else if (autoCorrect && validation.correctedValue !== undefined && correctedData) {
                correctedData.data.variables[key].default = validation.correctedValue;
              }
            }

            // Validate numeric constraints
            if (variable.min !== undefined && typeof variable.min !== 'number') {
              warnings.push(`Variable "${key}": min should be a number`);
            }
            if (variable.max !== undefined && typeof variable.max !== 'number') {
              warnings.push(`Variable "${key}": max should be a number`);
            }
            if (variable.step !== undefined && typeof variable.step !== 'number') {
              warnings.push(`Variable "${key}": step should be a number`);
            }
          }
        }
      }
    }

    // Validate timestamp
    if (data.timestamp !== undefined) {
      if (typeof data.timestamp !== 'number' || data.timestamp < 0) {
        errors.push('Timestamp must be a non-negative number');
      }
    }

    const result = {
      valid: errors.length === 0,
      errors,
      warnings
    };

    if (autoCorrect && correctedData && warnings.length > 0) {
      result.correctedData = correctedData;
    }

    return result;

  } catch (error) {
    errors.push(`Validation error: ${error.message}`);
    return { valid: false, errors, warnings };
  }
}

/**
 * Validates and sanitizes cosyflow data before storage
 * @param {object} data - The cosyflow data to validate and sanitize
 * @param {object} options - Validation options
 * @returns {Promise<object>} - { success: boolean, data?: object, errors?: string[] }
 */
export async function validateAndSanitizeCosyflowData(data, options = {}) {
  try {
    console.log('[Data Validation] Starting cosyflow data validation...');

    // Basic validation
    const validation = validateCosyflowData(data, { autoCorrect: true, ...options });

    if (!validation.valid) {
      console.error('[Data Validation] ❌ Validation failed:', validation.errors);
      electronBridge.log('[Data Validation] ❌ Validation failed: ' + validation.errors.join(', '));
      return { success: false, errors: validation.errors };
    }

    // Use corrected data if available
    const finalData = validation.correctedData || data;

    // Additional sanitization
    if (finalData.data && finalData.data.title) {
      // Trim whitespace from title
      finalData.data.title = finalData.data.title.trim();

      // Ensure timestamp is set
      if (!finalData.timestamp) {
        finalData.timestamp = Date.now();
      }
    }

    if (validation.warnings.length > 0) {
      console.warn('[Data Validation] ⚠️ Warnings:', validation.warnings);
      electronBridge.log('[Data Validation] ⚠️ Warnings: ' + validation.warnings.join(', '));
    }

    console.log('[Data Validation] ✅ Data validation passed');
    return { success: true, data: finalData, warnings: validation.warnings };

  } catch (error) {
    console.error('[Data Validation] ❌ Validation error:', error);
    electronBridge.log('[Data Validation] ❌ Validation error: ' + error);
    return { success: false, errors: [error.message] };
  }
}

/**
 * Safe storage functions with comprehensive validation
 * ===================================================
 */

/**
 * Safely adds a new cosyflow record with validation
 * @param {object} cosyflowData - The cosyflow data to add
 * @param {object} options - Storage options
 * @returns {Promise<object>} - { success: boolean, id?: number, errors?: string[], warnings?: string[] }
 */
export async function safeCosyflowAdd(cosyflowData, options = {}) {
  try {
    console.log('[Safe Storage] Adding new cosyflow record...');

    // Validate and sanitize the data first
    const validation = await validateAndSanitizeCosyflowData(cosyflowData, options);
    if (!validation.success) {
      return { success: false, errors: validation.errors };
    }

    const finalData = validation.data;

    // Check for duplicates if title is provided
    if (finalData.data?.title) {
      const existingRecord = await db.cosyflows.where('data.title').equals(finalData.data.title).first();
      if (existingRecord) {
        if (options.allowDuplicates !== true) {
          return {
            success: false,
            errors: [`Model with title "${finalData.data.title}" already exists (ID: ${existingRecord.id})`],
            existingId: existingRecord.id
          };
        } else {
          console.warn('[Safe Storage] Allowing duplicate title as per options');
        }
      }
    }

    // Add the record
    const recordId = await db.cosyflows.add(finalData);

    console.log(`[Safe Storage] ✅ Successfully added cosyflow record with ID: ${recordId}`);
    electronBridge.log(`[Safe Storage] ✅ Successfully added cosyflow record with ID: ${recordId}`);

    return {
      success: true,
      id: recordId,
      warnings: validation.warnings,
      data: finalData
    };

  } catch (error) {
    console.error('[Safe Storage] ❌ Error adding cosyflow record:', error);
    electronBridge.log('[Safe Storage] ❌ Error adding cosyflow record: ' + error);
    return { success: false, errors: [error.message] };
  }
}

/**
 * Safely updates an existing cosyflow record with validation
 * @param {number|string} id - The ID of the record to update
 * @param {object} updateData - The data to update
 * @param {object} options - Update options
 * @returns {Promise<object>} - { success: boolean, updated?: number, errors?: string[], warnings?: string[] }
 */
export async function safeCosyflowUpdate(id, updateData, options = {}) {
  try {
    console.log(`[Safe Storage] Updating cosyflow record ID: ${id}...`);

    // Get existing record first
    const existingRecord = await db.cosyflows.get(Number(id));
    if (!existingRecord) {
      return { success: false, errors: [`Record with ID ${id} not found`] };
    }

    // Merge update data with existing record
    const mergedData = {
      ...existingRecord,
      ...updateData,
      data: {
        ...existingRecord.data,
        ...updateData.data
      },
      timestamp: Date.now()
    };

    // Validate the merged data
    const validation = await validateAndSanitizeCosyflowData(mergedData, options);
    if (!validation.success) {
      return { success: false, errors: validation.errors };
    }

    const finalData = validation.data;

    // Check for title conflicts if title is being updated
    if (finalData.data?.title && finalData.data.title !== existingRecord.data?.title) {
      const conflictRecord = await db.cosyflows.where('data.title').equals(finalData.data.title).first();
      if (conflictRecord && conflictRecord.id !== Number(id)) {
        return {
          success: false,
          errors: [`Another model with title "${finalData.data.title}" already exists (ID: ${conflictRecord.id})`],
          conflictId: conflictRecord.id
        };
      }
    }

    // Update the record
    const updatedCount = await db.cosyflows.update(Number(id), finalData);

    if (updatedCount === 0) {
      return { success: false, errors: [`Failed to update record with ID ${id} - record may have been deleted`] };
    }

    console.log(`[Safe Storage] ✅ Successfully updated cosyflow record ID: ${id}`);
    electronBridge.log(`[Safe Storage] ✅ Successfully updated cosyflow record ID: ${id}`);

    return {
      success: true,
      updated: updatedCount,
      warnings: validation.warnings,
      data: finalData
    };

  } catch (error) {
    console.error(`[Safe Storage] ❌ Error updating cosyflow record ID ${id}:`, error);
    electronBridge.log(`[Safe Storage] ❌ Error updating cosyflow record ID ${id}: ` + error);
    return { success: false, errors: [error.message] };
  }
}

/**
 * Safely performs bulk operations on cosyflow records
 * @param {Array} operations - Array of { type: 'add'|'update'|'delete', data: object, id?: number }
 * @param {object} options - Bulk operation options
 * @returns {Promise<object>} - { success: boolean, results: Array, errors?: string[] }
 */
export async function safeCosyflowBulkOperation(operations, options = {}) {
  const { atomic = true } = options;
  const results = [];
  const errors = [];

  try {
    console.log(`[Bulk Storage] Starting bulk operation with ${operations.length} operations...`);

    if (atomic) {
      // Use transaction for atomic operations
      await db.transaction('rw', db.cosyflows, async () => {
        for (const [index, operation] of operations.entries()) {
          try {
            let result;
            switch (operation.type) {
              case 'add':
                result = await safeCosyflowAdd(operation.data, { ...options, skipTransaction: true });
                break;
              case 'update':
                result = await safeCosyflowUpdate(operation.id, operation.data, { ...options, skipTransaction: true });
                break;
              case 'delete':
                const deleteCount = await db.cosyflows.delete(operation.id);
                result = { success: deleteCount > 0, deleted: deleteCount };
                break;
              default:
                result = { success: false, errors: [`Unknown operation type: ${operation.type}`] };
            }
            results.push({ index, operation: operation.type, result });

            if (!result.success) {
              errors.push(`Operation ${index} (${operation.type}) failed: ${result.errors?.join(', ')}`);
              if (atomic) {
                throw new Error(`Bulk operation failed at index ${index}`);
              }
            }
          } catch (opError) {
            errors.push(`Operation ${index} threw error: ${opError.message}`);
            if (atomic) {
              throw opError;
            }
          }
        }
      });
    } else {
      // Non-atomic operations
      for (const [index, operation] of operations.entries()) {
        try {
          let result;
          switch (operation.type) {
            case 'add':
              result = await safeCosyflowAdd(operation.data, options);
              break;
            case 'update':
              result = await safeCosyflowUpdate(operation.id, operation.data, options);
              break;
            case 'delete':
              const deleteCount = await db.cosyflows.delete(operation.id);
              result = { success: deleteCount > 0, deleted: deleteCount };
              break;
            default:
              result = { success: false, errors: [`Unknown operation type: ${operation.type}`] };
          }
          results.push({ index, operation: operation.type, result });

          if (!result.success) {
            errors.push(`Operation ${index} (${operation.type}) failed: ${result.errors?.join(', ')}`);
          }
        } catch (opError) {
          errors.push(`Operation ${index} threw error: ${opError.message}`);
          results.push({ index, operation: operation.type, result: { success: false, errors: [opError.message] } });
        }
      }
    }

    const successCount = results.filter(r => r.result.success).length;
    const success = errors.length === 0;

    console.log(`[Bulk Storage] ${success ? '✅' : '⚠️'} Bulk operation completed: ${successCount}/${operations.length} successful`);
    electronBridge.log(`[Bulk Storage] Bulk operation completed: ${successCount}/${operations.length} successful`);

    return {
      success,
      results,
      errors: errors.length > 0 ? errors : undefined,
      summary: {
        total: operations.length,
        successful: successCount,
        failed: operations.length - successCount
      }
    };

  } catch (error) {
    console.error('[Bulk Storage] ❌ Bulk operation failed:', error);
    electronBridge.log('[Bulk Storage] ❌ Bulk operation failed: ' + error);
    return {
      success: false,
      results,
      errors: [error.message],
      summary: {
        total: operations.length,
        successful: 0,
        failed: operations.length
      }
    };
  }
}

/**
 * Validates all existing cosyflow records and repairs any issues
 * @param {object} options - Repair options
 * @returns {Promise<object>} - { success: boolean, report: object }
 */
export async function validateAndRepairCosyflowRecords(options = {}) {
  try {
    console.log('[Data Repair] Starting validation and repair of all cosyflow records...');

    const { dryRun = false, autoFix = true } = options;
    const report = {
      total: 0,
      valid: 0,
      invalid: 0,
      repaired: 0,
      failures: [],
      details: []
    };

    let recordCount = 0;
    const batchSize = 100;

    await db.cosyflows.orderBy('id').each(async (record) => {
      recordCount++;
      report.total++;

      const validation = validateCosyflowData(record, { autoCorrect: autoFix });

      if (validation.valid) {
        report.valid++;
      } else {
        report.invalid++;
        report.details.push({
          id: record.id,
          title: record.data?.title || 'Unknown',
          errors: validation.errors,
          warnings: validation.warnings
        });

        if (!dryRun && autoFix && validation.correctedData) {
          try {
            await db.cosyflows.update(record.id, validation.correctedData);
            report.repaired++;
            console.log(`[Data Repair] ✅ Repaired record ID ${record.id}: ${record.data?.title}`);
          } catch (repairError) {
            report.failures.push({
              id: record.id,
              title: record.data?.title || 'Unknown',
              error: repairError.message
            });
            console.error(`[Data Repair] ❌ Failed to repair record ID ${record.id}:`, repairError);
          }
        }
      }

      // Progress logging for large datasets
      if (recordCount % batchSize === 0) {
        console.log(`[Data Repair] Progress: ${recordCount} records processed...`);
      }
    });

    const success = report.failures.length === 0;

    console.log(`[Data Repair] ${success ? '✅' : '⚠️'} Validation complete: ${report.valid}/${report.total} valid, ${report.repaired} repaired, ${report.failures.length} failed`);
    electronBridge.log(`[Data Repair] Validation complete: ${report.valid}/${report.total} valid, ${report.repaired} repaired, ${report.failures.length} failed`);

    return { success, report };

  } catch (error) {
    console.error('[Data Repair] ❌ Validation and repair failed:', error);
    electronBridge.log('[Data Repair] ❌ Validation and repair failed: ' + error);
    return { success: false, error: error.message };
  }
}

/**
 * Advanced Error Handling & Retry Logic
 * ====================================
 */

/**
 * Error classification for different retry strategies
 */
const ErrorTypes = {
  NETWORK: 'network',
  TIMEOUT: 'timeout',
  SERVER_ERROR: 'server_error',
  CLIENT_ERROR: 'client_error',
  DATABASE: 'database',
  VALIDATION: 'validation',
  UNKNOWN: 'unknown'
};

/**
 * Classifies an error to determine retry strategy
 * @param {Error} error - The error to classify
 * @returns {string} - Error type from ErrorTypes
 */
function classifyError(error) {
  const message = error.message?.toLowerCase() || '';
  const code = error.code || error.status;

  // Network-related errors
  if (message.includes('network') || message.includes('fetch') || message.includes('connection') ||
      code === 'NETWORK_ERROR' || code === 'ERR_NETWORK') {
    return ErrorTypes.NETWORK;
  }

  // Timeout errors
  if (message.includes('timeout') || code === 'TIMEOUT' || code === 408) {
    return ErrorTypes.TIMEOUT;
  }

  // Server errors (5xx)
  if (code >= 500 && code < 600) {
    return ErrorTypes.SERVER_ERROR;
  }

  // Client errors (4xx) - usually not worth retrying
  if (code >= 400 && code < 500) {
    return ErrorTypes.CLIENT_ERROR;
  }

  // Database errors
  if (message.includes('database') || message.includes('indexeddb') || message.includes('dexie')) {
    return ErrorTypes.DATABASE;
  }

  // Validation errors
  if (message.includes('validation') || message.includes('invalid')) {
    return ErrorTypes.VALIDATION;
  }

  return ErrorTypes.UNKNOWN;
}

/**
 * Determines if an error is retryable based on its type
 * @param {string} errorType - Error type from ErrorTypes
 * @returns {boolean} - Whether the error should be retried
 */
function isRetryableError(errorType) {
  return [
    ErrorTypes.NETWORK,
    ErrorTypes.TIMEOUT,
    ErrorTypes.SERVER_ERROR,
    ErrorTypes.DATABASE,
    ErrorTypes.UNKNOWN
  ].includes(errorType);
}

/**
 * Implements exponential backoff with jitter
 * @param {number} attempt - Current attempt number (0-based)
 * @param {number} baseDelay - Base delay in milliseconds
 * @param {number} maxDelay - Maximum delay in milliseconds
 * @returns {number} - Delay in milliseconds
 */
function calculateBackoffDelay(attempt, baseDelay = 1000, maxDelay = 30000) {
  // Exponential backoff: delay = baseDelay * (2 ^ attempt)
  const exponentialDelay = baseDelay * Math.pow(2, attempt);

  // Cap at maxDelay
  const cappedDelay = Math.min(exponentialDelay, maxDelay);

  // Add jitter (±25% randomization to prevent thundering herd)
  const jitter = cappedDelay * 0.25 * (Math.random() - 0.5);

  return Math.floor(cappedDelay + jitter);
}

/**
 * Executes a function with exponential backoff retry logic
 * @param {Function} fn - The async function to execute
 * @param {object} options - Retry options
 * @returns {Promise<any>} - Result of the function or throws final error
 */
export async function withRetry(fn, options = {}) {
  const {
    maxAttempts = 3,
    baseDelay = 1000,
    maxDelay = 30000,
    retryCondition = null, // Custom function to determine if error should be retried
    onRetry = null, // Callback function called before each retry
    context = 'operation'
  } = options;

  let lastError = null;
  let attempt = 0;

  while (attempt < maxAttempts) {
    try {
      console.log(`[Retry Logic] ${context}: Attempt ${attempt + 1}/${maxAttempts}`);

      const result = await fn();

      if (attempt > 0) {
        console.log(`[Retry Logic] ✅ ${context}: Succeeded on attempt ${attempt + 1}`);
        electronBridge.log(`[Retry Logic] ✅ ${context}: Succeeded on attempt ${attempt + 1}`);
      }

      return result;

    } catch (error) {
      lastError = error;
      attempt++;

      console.warn(`[Retry Logic] ❌ ${context}: Attempt ${attempt} failed:`, error.message);

      // If this was the last attempt, don't wait or check retry conditions
      if (attempt >= maxAttempts) {
        console.error(`[Retry Logic] 🚫 ${context}: All ${maxAttempts} attempts failed`);
        electronBridge.log(`[Retry Logic] 🚫 ${context}: All ${maxAttempts} attempts failed: ${error.message}`);
        break;
      }

      // Determine if error should be retried
      let shouldRetry = true;

      if (retryCondition) {
        shouldRetry = retryCondition(error, attempt);
      } else {
        const errorType = classifyError(error);
        shouldRetry = isRetryableError(errorType);
        console.log(`[Retry Logic] Error type: ${errorType}, retryable: ${shouldRetry}`);
      }

      if (!shouldRetry) {
        console.log(`[Retry Logic] 🚫 ${context}: Error not retryable, stopping`);
        electronBridge.log(`[Retry Logic] 🚫 ${context}: Error not retryable: ${error.message}`);
        break;
      }

      const delay = calculateBackoffDelay(attempt - 1, baseDelay, maxDelay);
      console.log(`[Retry Logic] ⏳ ${context}: Waiting ${delay}ms before retry ${attempt + 1}...`);

      // Call onRetry callback if provided
      if (onRetry) {
        try {
          await onRetry(error, attempt, delay);
        } catch (callbackError) {
          console.warn(`[Retry Logic] Retry callback failed:`, callbackError);
        }
      }

      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // All attempts failed
  throw lastError;
}

/**
 * Circuit breaker implementation for protecting against cascading failures
 */
class CircuitBreaker {
  constructor(options = {}) {
    this.name = options.name || 'CircuitBreaker';
    this.failureThreshold = options.failureThreshold || 5;
    this.resetTimeout = options.resetTimeout || 60000; // 1 minute
    this.monitoringPeriod = options.monitoringPeriod || 30000; // 30 seconds

    this.state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN
    this.failureCount = 0;
    this.lastFailureTime = null;
    this.successCount = 0;
    this.nextAttemptTime = null;

    console.log(`[Circuit Breaker] ${this.name}: Initialized with threshold ${this.failureThreshold}`);
  }

  async execute(fn, context = 'operation') {
    if (this.state === 'OPEN') {
      if (Date.now() < this.nextAttemptTime) {
        const error = new Error(`Circuit breaker ${this.name} is OPEN. Next attempt at ${new Date(this.nextAttemptTime).toISOString()}`);
        error.circuitBreakerOpen = true;
        throw error;
      } else {
        this.state = 'HALF_OPEN';
        console.log(`[Circuit Breaker] ${this.name}: Transitioning to HALF_OPEN`);
      }
    }

    try {
      const result = await fn();
      this.onSuccess(context);
      return result;
    } catch (error) {
      this.onFailure(error, context);
      throw error;
    }
  }

  onSuccess(context) {
    this.failureCount = 0;
    this.successCount++;

    if (this.state === 'HALF_OPEN') {
      this.state = 'CLOSED';
      console.log(`[Circuit Breaker] ${this.name}: Transitioning to CLOSED after successful ${context}`);
      electronBridge.log(`[Circuit Breaker] ${this.name}: Transitioning to CLOSED`);
    }
  }

  onFailure(error, context) {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    console.warn(`[Circuit Breaker] ${this.name}: Failure ${this.failureCount}/${this.failureThreshold} for ${context}:`, error.message);

    if (this.failureCount >= this.failureThreshold && this.state !== 'OPEN') {
      this.state = 'OPEN';
      this.nextAttemptTime = Date.now() + this.resetTimeout;
      console.error(`[Circuit Breaker] ${this.name}: Opening circuit! Next attempt at ${new Date(this.nextAttemptTime).toISOString()}`);
      electronBridge.log(`[Circuit Breaker] ${this.name}: Circuit opened due to ${this.failureCount} failures`);
    }
  }

  getStatus() {
    return {
      name: this.name,
      state: this.state,
      failureCount: this.failureCount,
      successCount: this.successCount,
      lastFailureTime: this.lastFailureTime,
      nextAttemptTime: this.nextAttemptTime
    };
  }

  reset() {
    this.state = 'CLOSED';
    this.failureCount = 0;
    this.successCount = 0;
    this.lastFailureTime = null;
    this.nextAttemptTime = null;
    console.log(`[Circuit Breaker] ${this.name}: Manually reset`);
  }
}

// Global circuit breakers for different API endpoints
const circuitBreakers = {
  cosyflow: new CircuitBreaker({
    name: 'Cosyflow API',
    failureThreshold: 5,
    resetTimeout: 60000
  }),
  models: new CircuitBreaker({
    name: 'Models API',
    failureThreshold: 3,
    resetTimeout: 30000
  })
};

/**
 * Gets a circuit breaker instance by name
 * @param {string} name - Circuit breaker name
 * @returns {CircuitBreaker} - Circuit breaker instance
 */
export function getCircuitBreaker(name) {
  return circuitBreakers[name] || circuitBreakers.cosyflow;
}

/**
 * Enhanced API operation with retry logic and circuit breaker
 * @param {Function} apiFunction - The API function to execute
 * @param {object} options - Operation options
 * @returns {Promise<any>} - Result of API operation
 */
export async function resilientApiOperation(apiFunction, options = {}) {
  const {
    circuitBreakerName = 'cosyflow',
    retryOptions = {},
    context = 'API operation'
  } = options;

  const circuitBreaker = getCircuitBreaker(circuitBreakerName);

  return await circuitBreaker.execute(async () => {
    return await withRetry(apiFunction, {
      maxAttempts: 3,
      baseDelay: 1000,
      maxDelay: 10000,
      context,
      ...retryOptions,
      onRetry: async (error, attempt, delay) => {
        console.log(`[Resilient API] Retry attempt ${attempt} for ${context} after ${delay}ms`);
        electronBridge.log(`[Resilient API] Retry attempt ${attempt} for ${context}: ${error.message}`);

        // Call custom onRetry if provided
        if (retryOptions.onRetry) {
          await retryOptions.onRetry(error, attempt, delay);
        }
      }
    });
  }, context);
}

/**
 * Gets status of all circuit breakers
 * @returns {object} - Status of all circuit breakers
 */
export function getCircuitBreakerStatus() {
  const status = {};
  for (const [name, breaker] of Object.entries(circuitBreakers)) {
    status[name] = breaker.getStatus();
  }
  return status;
}

/**
 * Performance Metrics Collection System
 * ====================================
 */

class PerformanceMetrics {
  constructor() {
    this.metrics = {
      database: {
        lookupTimes: [],
        insertTimes: [],
        updateTimes: [],
        errorCounts: {},
        totalOperations: 0
      },
      api: {
        requestTimes: [],
        errorCounts: {},
        retryAttempts: [],
        circuitBreakerTrips: 0
      },
      validation: {
        validationTimes: [],
        validRecords: 0,
        invalidRecords: 0,
        repairAttempts: 0
      },
      system: {
        memoryUsage: [],
        performanceMarks: []
      }
    };

    this.startTime = Date.now();
    this.maxHistorySize = 1000; // Keep last 1000 entries for each metric

    console.log('[Performance Metrics] Initialized metrics collection system');
  }

  // Database Metrics
  recordDatabaseLookup(duration, success = true, operation = 'lookup') {
    const record = {
      timestamp: Date.now(),
      duration,
      success,
      operation
    };

    this.metrics.database.lookupTimes.push(record);
    this.metrics.database.totalOperations++;

    if (!success) {
      this.metrics.database.errorCounts[operation] = (this.metrics.database.errorCounts[operation] || 0) + 1;
    }

    this._trimArray(this.metrics.database.lookupTimes);
  }

  recordDatabaseInsert(duration, success = true) {
    const record = {
      timestamp: Date.now(),
      duration,
      success
    };

    this.metrics.database.insertTimes.push(record);
    this.metrics.database.totalOperations++;

    if (!success) {
      this.metrics.database.errorCounts.insert = (this.metrics.database.errorCounts.insert || 0) + 1;
    }

    this._trimArray(this.metrics.database.insertTimes);
  }

  recordDatabaseUpdate(duration, success = true) {
    const record = {
      timestamp: Date.now(),
      duration,
      success
    };

    this.metrics.database.updateTimes.push(record);
    this.metrics.database.totalOperations++;

    if (!success) {
      this.metrics.database.errorCounts.update = (this.metrics.database.errorCounts.update || 0) + 1;
    }

    this._trimArray(this.metrics.database.updateTimes);
  }

  // API Metrics
  recordApiRequest(duration, success = true, endpoint = 'unknown') {
    const record = {
      timestamp: Date.now(),
      duration,
      success,
      endpoint
    };

    this.metrics.api.requestTimes.push(record);

    if (!success) {
      this.metrics.api.errorCounts[endpoint] = (this.metrics.api.errorCounts[endpoint] || 0) + 1;
    }

    this._trimArray(this.metrics.api.requestTimes);
  }

  recordRetryAttempt(attempts, finalSuccess = true, context = 'unknown') {
    const record = {
      timestamp: Date.now(),
      attempts,
      finalSuccess,
      context
    };

    this.metrics.api.retryAttempts.push(record);
    this._trimArray(this.metrics.api.retryAttempts);
  }

  recordCircuitBreakerTrip() {
    this.metrics.api.circuitBreakerTrips++;
  }

  // Validation Metrics
  recordValidation(duration, valid = true, autoRepaired = false) {
    const record = {
      timestamp: Date.now(),
      duration,
      valid,
      autoRepaired
    };

    this.metrics.validation.validationTimes.push(record);

    if (valid) {
      this.metrics.validation.validRecords++;
    } else {
      this.metrics.validation.invalidRecords++;
    }

    if (autoRepaired) {
      this.metrics.validation.repairAttempts++;
    }

    this._trimArray(this.metrics.validation.validationTimes);
  }

  // System Metrics
  recordMemoryUsage() {
    if (typeof performance !== 'undefined' && performance.memory) {
      const memoryInfo = {
        timestamp: Date.now(),
        used: performance.memory.usedJSHeapSize,
        total: performance.memory.totalJSHeapSize,
        limit: performance.memory.jsHeapSizeLimit
      };

      this.metrics.system.memoryUsage.push(memoryInfo);
      this._trimArray(this.metrics.system.memoryUsage);
    }
  }

  recordPerformanceMark(name, data = {}) {
    const mark = {
      timestamp: Date.now(),
      name,
      data
    };

    this.metrics.system.performanceMarks.push(mark);
    this._trimArray(this.metrics.system.performanceMarks);
  }

  // Analysis Methods
  getAverageResponseTime(type = 'database', operation = 'lookup') {
    let times = [];

    switch (type) {
      case 'database':
        switch (operation) {
          case 'lookup':
            times = this.metrics.database.lookupTimes;
            break;
          case 'insert':
            times = this.metrics.database.insertTimes;
            break;
          case 'update':
            times = this.metrics.database.updateTimes;
            break;
        }
        break;
      case 'api':
        times = this.metrics.api.requestTimes;
        break;
      case 'validation':
        times = this.metrics.validation.validationTimes;
        break;
    }

    if (times.length === 0) return 0;

    const totalTime = times.reduce((sum, record) => sum + record.duration, 0);
    return totalTime / times.length;
  }

  getErrorRate(type = 'database', timeWindow = 300000) { // 5 minutes default
    const now = Date.now();
    const cutoff = now - timeWindow;

    let totalOperations = 0;
    let errorCount = 0;

    switch (type) {
      case 'database':
        const dbOperations = [
          ...this.metrics.database.lookupTimes,
          ...this.metrics.database.insertTimes,
          ...this.metrics.database.updateTimes
        ].filter(op => op.timestamp > cutoff);

        totalOperations = dbOperations.length;
        errorCount = dbOperations.filter(op => !op.success).length;
        break;

      case 'api':
        const apiOperations = this.metrics.api.requestTimes.filter(op => op.timestamp > cutoff);
        totalOperations = apiOperations.length;
        errorCount = apiOperations.filter(op => !op.success).length;
        break;

      case 'validation':
        const validationOperations = this.metrics.validation.validationTimes.filter(op => op.timestamp > cutoff);
        totalOperations = validationOperations.length;
        errorCount = validationOperations.filter(op => !op.valid).length;
        break;
    }

    return totalOperations > 0 ? (errorCount / totalOperations) * 100 : 0;
  }

  getPerformanceSummary() {
    const now = Date.now();
    const uptime = now - this.startTime;

    return {
      uptime: uptime,
      uptimeFormatted: this._formatDuration(uptime),
      database: {
        averageLookupTime: this.getAverageResponseTime('database', 'lookup'),
        averageInsertTime: this.getAverageResponseTime('database', 'insert'),
        averageUpdateTime: this.getAverageResponseTime('database', 'update'),
        errorRate: this.getErrorRate('database'),
        totalOperations: this.metrics.database.totalOperations,
        errorCounts: this.metrics.database.errorCounts
      },
      api: {
        averageResponseTime: this.getAverageResponseTime('api'),
        errorRate: this.getErrorRate('api'),
        totalRetries: this.metrics.api.retryAttempts.length,
        circuitBreakerTrips: this.metrics.api.circuitBreakerTrips,
        errorCounts: this.metrics.api.errorCounts
      },
      validation: {
        averageValidationTime: this.getAverageResponseTime('validation'),
        validRecords: this.metrics.validation.validRecords,
        invalidRecords: this.metrics.validation.invalidRecords,
        repairAttempts: this.metrics.validation.repairAttempts,
        successRate: this._calculateSuccessRate(
          this.metrics.validation.validRecords,
          this.metrics.validation.invalidRecords
        )
      },
      system: {
        memoryUsage: this._getLatestMemoryUsage(),
        performanceMarks: this.metrics.system.performanceMarks.slice(-10) // Last 10 marks
      }
    };
  }

  // Utility Methods
  _trimArray(array) {
    if (array.length > this.maxHistorySize) {
      array.splice(0, array.length - this.maxHistorySize);
    }
  }

  _formatDuration(ms) {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }

  _calculateSuccessRate(success, failure) {
    const total = success + failure;
    return total > 0 ? (success / total) * 100 : 100;
  }

  _getLatestMemoryUsage() {
    const latest = this.metrics.system.memoryUsage.slice(-1)[0];
    return latest || null;
  }

  // Reset Methods
  reset() {
    this.metrics = {
      database: {
        lookupTimes: [],
        insertTimes: [],
        updateTimes: [],
        errorCounts: {},
        totalOperations: 0
      },
      api: {
        requestTimes: [],
        errorCounts: {},
        retryAttempts: [],
        circuitBreakerTrips: 0
      },
      validation: {
        validationTimes: [],
        validRecords: 0,
        invalidRecords: 0,
        repairAttempts: 0
      },
      system: {
        memoryUsage: [],
        performanceMarks: []
      }
    };

    this.startTime = Date.now();
    console.log('[Performance Metrics] Metrics reset');
  }

  // Export methods
  exportMetrics() {
    return {
      exportTime: Date.now(),
      summary: this.getPerformanceSummary(),
      rawMetrics: this.metrics
    };
  }
}

// Global metrics instance
const performanceMetrics = new PerformanceMetrics();

// Automatic memory usage collection every 30 seconds
if (typeof setInterval !== 'undefined') {
  setInterval(() => {
    performanceMetrics.recordMemoryUsage();
  }, 30000);
}

/**
 * Gets the global performance metrics instance
 * @returns {PerformanceMetrics} - The metrics instance
 */
export function getPerformanceMetrics() {
  return performanceMetrics;
}

/**
 * Decorator function to automatically measure and record operation performance
 * @param {string} type - Type of operation (database, api, validation)
 * @param {string} operation - Specific operation name
 * @returns {Function} - Decorator function
 */
export function measurePerformance(type, operation = 'unknown') {
  return function(target, propertyKey, descriptor) {
    const originalMethod = descriptor.value;

    descriptor.value = async function(...args) {
      const startTime = performance.now();
      let success = true;
      let error = null;

      try {
        const result = await originalMethod.apply(this, args);
        return result;
      } catch (err) {
        success = false;
        error = err;
        throw err;
      } finally {
        const duration = performance.now() - startTime;

        switch (type) {
          case 'database':
            performanceMetrics.recordDatabaseLookup(duration, success, operation);
            break;
          case 'api':
            performanceMetrics.recordApiRequest(duration, success, operation);
            break;
          case 'validation':
            performanceMetrics.recordValidation(duration, success);
            break;
        }

        if (!success && error) {
          console.warn(`[Performance] ${type}:${operation} failed in ${duration.toFixed(2)}ms:`, error.message);
        }
      }
    };

    return descriptor;
  };
}

/**
 * Simple performance timing utility for non-decorated functions
 * @param {Function} fn - Function to measure
 * @param {string} type - Type of operation
 * @param {string} operation - Operation name
 * @returns {Promise<any>} - Result of the function
 */
export async function measureAndRecord(fn, type, operation = 'unknown') {
  const startTime = performance.now();
  let success = true;

  try {
    const result = await fn();
    return result;
  } catch (error) {
    success = false;
    throw error;
  } finally {
    const duration = performance.now() - startTime;

    switch (type) {
      case 'database':
        performanceMetrics.recordDatabaseLookup(duration, success, operation);
        break;
      case 'api':
        performanceMetrics.recordApiRequest(duration, success, operation);
        break;
      case 'validation':
        performanceMetrics.recordValidation(duration, success);
        break;
    }
  }
}

// Database validation and diagnostic functions for cosyflows indexing
export async function validateCosyflowsIndexing() {
  try {
    console.log('[DB Validation] Starting cosyflows indexing validation...');
    electronBridge.log('[DB Validation] Starting cosyflows indexing validation...');

    // Wait for database to be ready
    await db.open();

    // Get current database version
    const currentVersion = db.verno;
    console.log('[DB Validation] Current database version:', currentVersion);
    electronBridge.log('[DB Validation] Current database version: ' + currentVersion);

    // Check if cosyflows table exists
    const tableExists = db.tables.some(table => table.name === 'cosyflows');
    if (!tableExists) {
      console.error('[DB Validation] ❌ cosyflows table does not exist!');
      electronBridge.log('[DB Validation] ❌ cosyflows table does not exist!');
      return { success: false, error: 'cosyflows table not found' };
    }

    // Test if indexing is working by performing sample queries
    const testTitle = 'test-model-' + Date.now();
    let testPassed = false;

    try {
      // Add a test record
      const testRecord = {
        data: { title: testTitle, variables: {} },
        timestamp: Date.now()
      };

      const recordId = await db.cosyflows.add(testRecord);
      console.log('[DB Validation] Added test record with ID:', recordId);

      // Test indexed lookup
      const startTime = performance.now();
      const foundRecord = await db.cosyflows.where('data.title').equals(testTitle).first();
      const lookupTime = performance.now() - startTime;

      if (foundRecord && foundRecord.data.title === testTitle) {
        console.log(`[DB Validation] ✅ Indexed lookup working! Query time: ${lookupTime.toFixed(2)}ms`);
        electronBridge.log(`[DB Validation] ✅ Indexed lookup working! Query time: ${lookupTime.toFixed(2)}ms`);
        testPassed = true;
      } else {
        console.error('[DB Validation] ❌ Indexed lookup failed - record not found via index');
        electronBridge.log('[DB Validation] ❌ Indexed lookup failed - record not found via index');
      }

      // Clean up test record
      await db.cosyflows.delete(recordId);

    } catch (indexTestError) {
      console.error('[DB Validation] ❌ Index test failed:', indexTestError);
      electronBridge.log('[DB Validation] ❌ Index test failed: ' + indexTestError);
    }

    // Get count of total records
    const totalRecords = await db.cosyflows.count();
    console.log('[DB Validation] Total cosyflow records:', totalRecords);
    electronBridge.log('[DB Validation] Total cosyflow records: ' + totalRecords);

    // Validate data integrity - check for records with missing or invalid data.title
    let validRecords = 0;
    let invalidRecords = 0;

    await db.cosyflows.each(record => {
      if (record.data && typeof record.data.title === 'string' && record.data.title.length > 0) {
        validRecords++;
      } else {
        invalidRecords++;
        console.warn('[DB Validation] Found invalid record:', record.id, record.data?.title);
      }
    });

    console.log(`[DB Validation] Data integrity check: ${validRecords} valid, ${invalidRecords} invalid records`);
    electronBridge.log(`[DB Validation] Data integrity check: ${validRecords} valid, ${invalidRecords} invalid records`);

    const result = {
      success: testPassed && invalidRecords === 0,
      version: currentVersion,
      totalRecords,
      validRecords,
      invalidRecords,
      indexingWorking: testPassed
    };

    if (result.success) {
      console.log('[DB Validation] ✅ All validation checks passed!');
      electronBridge.log('[DB Validation] ✅ All validation checks passed!');
    } else {
      console.warn('[DB Validation] ⚠️ Some validation checks failed');
      electronBridge.log('[DB Validation] ⚠️ Some validation checks failed');
    }

    return result;

  } catch (error) {
    console.error('[DB Validation] ❌ Validation failed with error:', error);
    electronBridge.log('[DB Validation] ❌ Validation failed with error: ' + error);
    return { success: false, error: error.message };
  }
}

// Performance comparison function to demonstrate the improvement
export async function compareLookupPerformance(modelTitle) {
  if (!modelTitle) {
    // Find a random model to test with
    const allRecords = await db.cosyflows.limit(1).toArray();
    if (allRecords.length === 0) {
      console.log('[Performance Test] No records available for testing');
      return null;
    }
    modelTitle = allRecords[0].data?.title;
  }

  if (!modelTitle) {
    console.log('[Performance Test] No valid model title found for testing');
    return null;
  }

  console.log('[Performance Test] Testing lookup performance for model:', modelTitle);

  try {
    // Test old method (O(n) - full table scan)
    const oldMethodStart = performance.now();
    const allRecords = await db.cosyflows.toArray();
    const oldResult = allRecords.find(record => record.data?.title === modelTitle);
    const oldMethodTime = performance.now() - oldMethodStart;

    // Test new method (O(log n) - indexed lookup)
    const newMethodStart = performance.now();
    const newResult = await db.cosyflows.where('data.title').equals(modelTitle).first();
    const newMethodTime = performance.now() - newMethodStart;

    const improvement = oldMethodTime / newMethodTime;

    const results = {
      modelTitle,
      oldMethodTime: oldMethodTime.toFixed(2),
      newMethodTime: newMethodTime.toFixed(2),
      improvement: improvement.toFixed(1),
      oldResultFound: !!oldResult,
      newResultFound: !!newResult,
      totalRecords: allRecords.length
    };

    console.log('[Performance Test] Results:', results);
    console.log(`[Performance Test] 🚀 New method is ${improvement.toFixed(1)}x faster!`);
    electronBridge.log(`[Performance Test] New method is ${improvement.toFixed(1)}x faster! (${oldMethodTime.toFixed(2)}ms → ${newMethodTime.toFixed(2)}ms)`);

    return results;

  } catch (error) {
    console.error('[Performance Test] Error during performance comparison:', error);
    electronBridge.log('[Performance Test] Error: ' + error);
    return null;
  }
}

// Version management functions to prevent IndexedDB mismatches
async function checkAppVersion() {
  try {
    // Get the current app version from environment
    const currentAppVersion = process.env.REACT_APP_VERSION || '1.0.0';
    
    // Open database to check stored version
    await db.open();
    
    // Get stored app version
    const storedVersionEntry = await db.settings.get('appVersion');
    const storedAppVersion = storedVersionEntry?.value;
    
    // Get stored DB version
    const storedDbVersionEntry = await db.settings.get('dbVersion');
    const storedDbVersion = storedDbVersionEntry?.value || 0;
    
    console.log('Version check:', {
      currentAppVersion,
      storedAppVersion,
      currentDbVersion: db.verno,
      storedDbVersion
    });
    
    // If versions don't match, handle the mismatch
    if (storedAppVersion && storedAppVersion !== currentAppVersion) {
      console.log('App version mismatch detected. Handling update...');
      await handleVersionMismatch(currentAppVersion, storedAppVersion);
    } else if (!storedAppVersion) {
      // First time running, store current versions
      await db.settings.put({ key: 'appVersion', value: currentAppVersion });
      await db.settings.put({ key: 'dbVersion', value: db.verno });
      console.log('Initial app version stored:', currentAppVersion);
    }
    
    // Always update DB version if it changed
    if (storedDbVersion !== db.verno) {
      await db.settings.put({ key: 'dbVersion', value: db.verno });
      console.log('DB version updated:', db.verno);
    }
    
  } catch (error) {
    console.error('Error checking app version:', error);
    electronBridge.log('Error checking app version:', error);
  }
}

async function handleVersionMismatch(currentVersion, storedVersion) {
  console.log(`Handling version update from ${storedVersion} to ${currentVersion}`);
  electronBridge.log(`Handling version update from ${storedVersion} to ${currentVersion}`);
  
  try {
    // Update stored version
    await db.settings.put({ key: 'appVersion', value: currentVersion });
    await db.settings.put({ key: 'dbVersion', value: db.verno });
    
    // Clear browser caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('Browser caches cleared');
    }
    
    // Force reload to ensure fresh assets
    console.log('Version updated. Reloading application...');
    electronBridge.log('Version updated. Reloading application...');
    
    // Show user notification before reload
    const shouldReload = window.confirm(
      `Application has been updated from version ${storedVersion} to ${currentVersion}.\n\n` +
      'Please click OK to reload and complete the update.'
    );
    
    if (shouldReload) {
      window.location.reload(true);
    }
    
  } catch (error) {
    console.error('Error handling version mismatch:', error);
    electronBridge.log('Error handling version mismatch:', error);
  }
}

// Call version check after database initialization
checkAppVersion();

// Run validation and performance testing after initialization (only in development)
if (process.env.NODE_ENV === 'development') {
  // Add a small delay to ensure DB is fully initialized
  setTimeout(async () => {
    try {
      console.log('[DB] Running post-initialization validation and performance tests...');

      // Run validation
      const validationResult = await validateCosyflowsIndexing();

      if (validationResult.success) {
        console.log('[DB] 🎉 Database validation passed! Indexing is working correctly.');

        // Run performance comparison if we have data
        if (validationResult.totalRecords > 0) {
          await compareLookupPerformance();
        }
      } else {
        console.warn('[DB] ⚠️ Database validation failed:', validationResult);
      }

    } catch (error) {
      console.error('[DB] Error during post-initialization validation:', error);
    }
  }, 2000);
}

// Add after the initDatabase function
let recommendedSettingsInitialized = false;

// Helper function to ensure a table exists
export async function ensureTableExists(tableName, schema) {
  try {
    const tableExists = db.tables.some(table => table.name === tableName);
    
    if (!tableExists) {
      console.log(`Table ${tableName} doesn't exist, creating it now`);
      electronBridge.log( `Table ${tableName} doesn't exist, creating it now`);
      
      // Create a new version with the table
      const newVersion = db.verno + 1;
      const schemaObj = {};
      schemaObj[tableName] = schema;
      
      db.version(newVersion).stores(schemaObj);
      
      // Reopen the database to apply the changes
      await db.open();
      console.log(`Table ${tableName} created successfully`);
      electronBridge.log( `Table ${tableName} created successfully`);
      
      return true;
    }
    
    return true; // Table already exists
  } catch (error) {
    console.error(`Error ensuring ${tableName} table exists:`, error);
    electronBridge.log( `Error ensuring ${tableName} table exists: ${error}`);
    return false;
  }
}

export async function initRecommendedSettings(settingsData) {
  // Skip if already initialized to prevent duplicate operations
  if (recommendedSettingsInitialized) {
    console.log("Recommended settings already initialized");
    return;
  }

  try {
    // Ensure the database is ready
    await db.open();

    // Process provided settings data
    if (settingsData && Array.isArray(settingsData)) {
      for (const item of settingsData) {
        let model = item.model.trim();

        try {
          // Check if setting already exists
          const existingSetting = await db.recommendedSettings
            .where('model')
            .equals(model)
            .first();

          if (existingSetting === undefined) {
            const setting = {
              model: model,
              steps: parseFloat(item.steps),
              guidance: parseFloat(item.guidance),
              imageCount: parseInt(item.imageCount),
              imageWidth: parseInt(item.imageWidth),
              imageHeight: parseInt(item.imageHeight),
              scheduler: item.scheduler.trim(),
              tile: parseInt(item.tile),
              shuffle: parseInt(item.shuffle),
              upscale: parseInt(item.upscale)
            };

            try {
              await db.recommendedSettings.add(setting);
            } catch (error) {
              if (error.name !== "ConstraintError") {
                console.error('Error adding setting:', error);
              }
            }
          }
        } catch (error) {
          console.error('Error checking existing setting:', error);
        }
      }
    }

    // Mark as initialized to prevent duplicate calls
    recommendedSettingsInitialized = true;
    console.log("Recommended settings initialized successfully");
  } catch (error) {
    console.error('Error initializing recommended settings:', error);
  }
}

export async function updateRecommendedSettings(apiSettings) {
  try {
    if (!apiSettings || !Array.isArray(apiSettings)) {
      console.log("No API settings to update");
      return;
    }

    await db.transaction('rw', db.recommendedSettings, async () => {
      for (const setting of apiSettings) {
        if (!setting.model) continue;
        
        const existingSetting = await db.recommendedSettings
          .where('model')
          .equals(setting.model)
          .first();

        const dbSetting = {
          model: setting.model,
          steps: parseFloat(setting.steps || setting.steps),
          guidance: parseFloat(setting.guidance || setting.guidance_scale),
          imageCount: parseInt(setting.image_count || setting.imageCount || 1),
          imageWidth: parseInt(setting.image_width || setting.imageWidth || 512),
          imageHeight: parseInt(setting.image_height || setting.imageHeight || 512),
          scheduler: setting.scheduler || "DPM-Solver",
          tile: parseInt(setting.tile || 0),
          shuffle: parseInt(setting.shuffle || 0),
          upscale: parseInt(setting.upscale || 0),
        };

        if (existingSetting) {
          // Update the existing setting
          await db.recommendedSettings.update(existingSetting.id, dbSetting);
        } else {
          // Add the new setting
          try {
            await db.recommendedSettings.add(dbSetting);
          } catch (error) {
            if (error.name !== "ConstraintError") {
              console.error('Error adding API setting:', error);
            }
          }
        }
      }
    });

    console.log('Recommended settings updated from API successfully');
  } catch (error) {
    console.error('Error updating recommended settings from API:', error);
  }
}